/* eslint-disable max-lines */
import {
  calcWorkBreakDownData,
  getWorkBreakdownConfigurations
} from '../../../dashboard/util';
import { CycleTime, CycleTimePipe } from './cycleTimeUtil';
import { Tooltip } from '@material-ui/core';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import { getColor } from 'common/constants/_colors';
import {
  DATE_FORMAT_WITH_FULL_DAY,
  EMPTY_NUMBER,
  EMPTY_VALUE,
  prStateToIconMap,
  SHORT_DATE_FORMAT
} from 'common/constants/constants';
import { ACCOUNT_TYPES_MAP, metricNames } from '@linearb/linjs-common';
import { ICON_SIZES, SPACES } from 'common/constants/sizes';
import {
  columnSummaryMapper,
  getLastActivityPhrase,
  nullSafeGet,
  numeralify,
  isViewerAccount
} from 'common/utils/utils';
import GhostAvatar from 'components/GhostAvatar';
import MultipleAvatars from 'components/MultipleAvatars';
import PieChart from 'components/PieChart';
import { EffectiveContributors } from 'components/Tooltips/EffectiveContributors/EffectiveContributors';
import { getEffectiveContirbutorsAvatars } from 'components/Tooltips/EffectiveContributors/effective-contributors.service';
import Chip from 'components/base/Chip';
import { HyperLink } from 'components/base/HyperLink';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import { Flex, Separator } from 'components/base/layout-components';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import {
  PIE_SIZE,
  TABLE_COLUMNS,
  stateToChipTypeMap,
  branchStateToChipTypeMap,
  disableCycleTimeExclusionFunctionality
} from './branches.service';
import { WorkBreakdownPopupTooltip } from 'components/Tooltips/WorkBreakdownTooltip';
import ActionsMenuCell from './components/ActionsMenuCell';
import WorkBreakdownCell from './components/WorkBreakdownCell';

const ClickableCell = styled.div`
  cursor: pointer;
`;

export const getColumns = _.memoize(
  ({
    branches,
    isCycleTimeColsExpanded,
    isWorkBreakdownColsExpanded,
    toggleIsCycleTimeColsExpanded,
    toggleIsWorkBreakdownColsExpanded,
    isExcludeBranches,
    branchMetricsPerPrIssuerEnabled,
    customStages = []
  } = {}) => {
    return [
      {
        dataPoint: TABLE_COLUMNS.NAME,
        name: 'BRANCHES',
        summary: 'total',
        tableCellProps: {
          style: {
            borderRight: 0,
            minWidth: '10rem'
          }
        }
      },
      {
        dataPoint: TABLE_COLUMNS.STATE,
        name: 'STATE',
        tableCellProps: {
          style: {
            borderLeft: 0
          }
        }
      },
      {
        dataPoint: TABLE_COLUMNS.CONTRIBUTORS,
        name: 'CONTRIBUTORS',
        summary: 'summary.contributors'
      },
      ...(branchMetricsPerPrIssuerEnabled
        ? [
            {
              dataPoint: TABLE_COLUMNS.PR_ISSUER,
              name: 'PR ISSUER'
            }
          ]
        : []),
      {
        dataPoint: TABLE_COLUMNS.REPO_NAME,
        name: 'REPOSITORIES',
        summary: 'summary.repositories',
        tableCellProps: {
          style: {
            minWidth: '5rem'
          }
        }
      },
      {
        dataPoint: TABLE_COLUMNS.CYCLE_TIME,
        name: 'CYCLE TIME',
        isSortedCol: true,
        tableCellProps: {
          style: {
            borderLeft: 0,
            minWidth: '6rem',
            borderRight: 0
          }
        },
        expandProps: {
          toggleFn: toggleIsCycleTimeColsExpanded,
          isOpen: isCycleTimeColsExpanded,
          columns: [
            {
              dataPoint: TABLE_COLUMNS.TIME_TO_PR,
              name: 'CODING TIME',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0,
                  borderRight: 0
                }
              }
            },
            {
              dataPoint: TABLE_COLUMNS.TIME_TO_REVIEW,
              name: 'PICK UP TIME',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0,
                  borderRight: 0
                }
              }
            },
            {
              dataPoint: TABLE_COLUMNS.REVIEW_TIME,
              name: 'REVIEW TIME',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0,
                  borderRight: 0
                }
              }
            },
            {
              dataPoint: TABLE_COLUMNS.TIME_TO_PROD,
              name: 'DEPLOY TIME',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0
                }
              }
            },
            ...customStages.map(({ description = '', metric_name }) => {
              return {
                dataPoint: metric_name,
                name: description.toUpperCase(),
                isSortedCol: true,
                tableCellProps: {
                  style: {
                    borderLeft: 0,
                    borderRight: 0
                  }
                }
              };
            })
          ]
        }
      },
      {
        dataPoint: TABLE_COLUMNS.COMMIT_COUNT,
        name: 'COMMITS',
        isSortedCol: true,
        summary: 'summary.commit_count',
        tableCellProps: {
          style: {
            borderRight: 0,
            paddingRight: 1
          }
        }
      },
      {
        dataPoint: TABLE_COLUMNS.WORK_BREAKDOWN,
        name: 'WORK BREAKDOWN',
        tableCellProps: {
          style: {
            width: `calc(${PIE_SIZE.width}px + 2.4rem)`,
            borderRight: 0
          }
        },
        expandProps: {
          toggleFn: toggleIsWorkBreakdownColsExpanded,
          isOpen: isWorkBreakdownColsExpanded,
          columns: [
            {
              dataPoint: 'newWork',
              name: 'NEW',
              isSortedCol: true,
              sortPoint: 'new_work',
              tableCellProps: {
                style: {
                  borderLeft: 1,
                  borderRight: 0
                }
              }
            },
            {
              dataPoint: 'rework',
              name: 'REWORK',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0,
                  borderRight: 0
                }
              }
            },
            {
              dataPoint: 'refactor',
              name: 'REFACTOR',
              isSortedCol: true,
              tableCellProps: {
                style: {
                  borderLeft: 0,
                  borderRight: 1
                }
              }
            }
          ]
        }
      },
      {
        dataPoint: TABLE_COLUMNS.PR_SIZE,
        name: 'PR SIZE',
        isSortedCol: true,
        tableCellProps: {
          style: {
            borderRight: 0
          }
        }
      },
      {
        dataPoint: TABLE_COLUMNS.PR_STATE,
        name: 'PR',
        summary: 'summary.has_pull_requests',
        tableCellProps: {
          style: {
            borderLeft: 0,
            borderRight: 0
          }
        }
      },
      isExcludeBranches
        ? {
            dataPoint: TABLE_COLUMNS.ACTIONS,
            name: '',
            showOnlyOnHover: true,
            tableCellProps: {
              style: {
                borderLeft: 0
              }
            }
          }
        : null
    ]
      .filter(Boolean)
      .map(column => columnSummaryMapper(column, branches, 'middle'));
  }
);

// eslint-disable-next-line max-lines-per-function
export const getBranchesSnapshotData = ({
  data,
  contributorsById,
  repositoriesByIds,
  onRepositoryClick = () => null,
  onContributorClick = () => null,
  repositories = [],
  gitIntegrations = [],
  myAccount,
  toggleModal,
  toggleSnackBar,
  isExcludeBranches,
  onBranchUpdate,
  isHideNonWorkingDaysEnabled,
  showPersonalInformation,
  branchesCycleTimeStages = {},
  metricName = null,
  isCycleTimeInspectionEnabled = false,

  customStages = []
}) =>
  // eslint-disable-next-line max-lines-per-function
  data.map(branch => {
    const {
      effective_contributors,
      contributors = [],
      stats,
      state: branchState,
      code_changes,
      pr_size: prSize,
      pull_request: {
        is_draft: isDraft = false,
        state: pr_state = EMPTY_VALUE,
        html_url: prUrl = null,
        issuer_id: issuerId
      } = {},
      branch_name = EMPTY_VALUE,
      release: {
        name: releaseName,
        published_at: releaseDate,
        stage: releaseStage
      } = {},
      merged_at: mergedAt,
      repository_id: repositoryId,
      last_activity: lastActivity,
      first_committed_at: firstCommittedAt,
      updated_at: updatedAt,
      commit_count: commitCount,
      extra
    } = branch || {};

    const repositoryName = _.get(
      repositoriesByIds,
      [repositoryId, 'name'],
      EMPTY_VALUE
    );

    const repositoryUrl = _.get(
      repositoriesByIds,
      [repositoryId, 'http_url'],
      null
    );
    const metricsIncludedWeekend = _.get(
      branch,
      'metrics_included_weekends',
      {}
    );

    const {
      [metricNames.REWORK]: rework = EMPTY_NUMBER,
      [metricNames.REFACTOR]: refactor = EMPTY_NUMBER,
      [metricNames.NEW_WORK]: newWork = EMPTY_NUMBER
    } = stats || {};
    const branchContributors = contributors.map(contId => {
      const { name, id, extended_data, email } = contributorsById[contId] || {};
      return {
        url: getContributorAvatarUrl({ id, extended_data }),
        name,
        id,
        email
      };
    });
    const branchEffectiveContributors = !effective_contributors
      ? null
      : effective_contributors.map(contId => {
          const { name, id, extended_data, email } =
            contributorsById[contId] || {};
          return {
            url: getContributorAvatarUrl({ id, extended_data }),
            name,
            id,
            email
          };
        });
    const prIssuerData = _.get(contributorsById, issuerId, {});
    const prIssuer = issuerId && {
      url: getContributorAvatarUrl(prIssuerData),
      name: prIssuerData.name,
      email: prIssuerData.email || '',
      id: prIssuerData.id
    };

    const { type: last_activity_type, author_id } = lastActivity || {};
    const prState = isDraft ? 'draft' : pr_state;
    const branchName = branch_name.replace(/origin\//g, '');

    const repoIntegrationId = _.get(
      repositories.find(({ id }) => id === repositoryId),
      'sensor_authorization_id',
      ''
    );
    const repoIntegrationName = _.get(
      gitIntegrations.find(({ id }) => id === repoIntegrationId),
      'name',
      ''
    );
    const lastActivityType =
      last_activity_type && prStateToIconMap[last_activity_type];
    const lastActivityProviderId = _.get(
      contributorsById[author_id],
      'extended_data.provider_id',
      null
    );
    const accountProviderId = nullSafeGet(
      myAccount,
      'settings.provider_id',
      EMPTY_NUMBER
    ).toString();

    const getBranchStateTooltip = () => {
      switch (branchState) {
        case 'deployed':
          return `${
            releaseDate
              ? `Released at ${moment(releaseDate).format(SHORT_DATE_FORMAT)}${
                  releaseName ? ` (${releaseName})` : ''
                }`
              : releaseName
              ? `Release tag: ${releaseName}`
              : ''
          }`;
        case 'merged':
          return mergedAt
            ? `Merged at ${moment(mergedAt).format(SHORT_DATE_FORMAT)}`
            : null;
        default:
          return null;
      }
    };
    const stateTooltip = getBranchStateTooltip();
    const stateText = `
      ${branchState}
      ${releaseStage ? `(${releaseStage})` : ''}
    `;

    const { avatars, showGhostAvatar } = getEffectiveContirbutorsAvatars({
      contributors: branchContributors,
      effectiveContributors: branchEffectiveContributors
    });

    const getCustomEndingAvatar = props => (
      <GhostAvatar key={_.get(props, 'idx')} {...props}></GhostAvatar>
    );

    const workBreakdownPercents = calcWorkBreakDownData({
      newWork,
      rework,
      refactor
    });
    const workBreakdownConfig = {
      newWork: {
        title: 'New',
        color: getColor('pastelGreen'),
        value: newWork,
        percents: _.get(workBreakdownPercents, 'newWork', 0)
      },
      refactor: {
        title: 'Refactor',
        color: getColor('indigo'),
        value: refactor,
        percents: _.get(workBreakdownPercents, 'refactor', 0)
      },
      rework: {
        title: 'Rework',
        color: getColor('melrose'),
        value: rework,
        percents: _.get(workBreakdownPercents, 'rework', 0)
      }
    };

    const noWorkBreakdown =
      _.sum(_.values(workBreakdownConfig).map(({ value }) => value)) === 0;

    return {
      contributors: (
        <Tooltip
          title={
            <EffectiveContributors
              contributors={branchContributors}
              effectiveContributors={branchEffectiveContributors}
              branchName={branchName}
              prUrl={prUrl}
            />
          }
          interactive={true}
        >
          <div>
            <MultipleAvatars
              disableTooltip={true}
              avatars={avatars}
              onClick={showPersonalInformation && onContributorClick}
              getCustomEndingAvatar={
                showGhostAvatar ? getCustomEndingAvatar : null
              }
              size={ICON_SIZES['x-medium']}
            />
          </div>
        </Tooltip>
      ),
      [TABLE_COLUMNS.PR_ISSUER]: (
        <MultipleAvatars
          avatars={prIssuer ? [prIssuer] : []}
          onClick={showPersonalInformation && onContributorClick}
          size={ICON_SIZES['x-medium']}
        />
      ),
      branch_name: (
        <>
          <Tooltip
            title={
              <Flex direction='column' items='flex-start' padding='x-x-small'>
                <Flex items='flex-start'>
                  <Icon name='Branch' size='smaller' color='shuttleGray' />
                  <Text size='x-medium' pb='1rem' pl='0.5rem'>
                    {branchName}
                  </Text>
                </Flex>
                <Text size='medium' pb='0.5rem'>
                  Created{' '}
                  {moment(firstCommittedAt).format(DATE_FORMAT_WITH_FULL_DAY)}
                </Text>
                <Text size='medium'>
                  Updated {moment(updatedAt).format(DATE_FORMAT_WITH_FULL_DAY)}
                </Text>
              </Flex>
            }
          >
            {['closed', 'deleted'].includes(branchState) || !prUrl ? (
              <Text size='large' multiLineEllipsisAfter={2}>
                {branchName}
              </Text>
            ) : (
              <HyperLink
                target='_blank'
                rel='noopener noreferrer'
                href={prUrl}
                multiLineEllipsisAfter={2}
              >
                <Text size='large' color='chambray'>
                  {branchName}
                </Text>
              </HyperLink>
            )}
          </Tooltip>
          {lastActivity && (
            <Flex items='center' pt='x-small'>
              {lastActivityType && (
                <Icon
                  name={lastActivityType.name}
                  size='x-small'
                  color={lastActivityType.color}
                />
              )}
              <Text color='gray' pl={SPACES['x-small']}>
                {getLastActivityPhrase(
                  accountProviderId,
                  lastActivityProviderId,
                  lastActivity,
                  'author_id',
                  contributorsById
                )}
              </Text>
            </Flex>
          )}
        </>
      ),
      state:
        branchState === EMPTY_VALUE ? (
          <Text size='large' align='center'>
            {EMPTY_VALUE}
          </Text>
        ) : (
          <Tooltip
            title={stateTooltip || ''}
            min-width='10rem'
            disableHoverListener={!stateTooltip}
          >
            <Flex>
              <Chip
                label={stateText}
                size='auto'
                variant='outlined'
                color={branchStateToChipTypeMap[branchState]}
              />
            </Flex>
          </Tooltip>
        ),
      repository_name: (
        <Tooltip
          title={
            <Flex direction='column' padding='x-x-small' minWidth='15rem'>
              <Text size='large' color='black'>
                {repositoryName}
              </Text>
              <Separator
                direction='horizontal'
                width='100%'
                height='1px'
                margin='1rem 0'
              ></Separator>
              <Text size='medium' color='gray' pb='0.4rem'>
                In integration:
              </Text>
              {repoIntegrationName}
            </Flex>
          }
          disableHoverListener={gitIntegrations.length <= 1}
        >
          <ClickableCell onClick={() => onRepositoryClick(repositoryUrl)}>
            <Text
              size='large'
              cursor='pointer'
              color='chambray'
              multiLineEllipsisAfter={2}
            >
              {repositoryName}
            </Text>
          </ClickableCell>
        </Tooltip>
      ),
      commit_count: <Text size='large'>{commitCount}</Text>,
      [TABLE_COLUMNS.WORK_BREAKDOWN]: (
        <Flex width='100%' height='100%' items='center' justify='center'>
          <WorkBreakdownPopupTooltip
            disableHoverListener={noWorkBreakdown}
            title={
              <Text weight='500' size='x-x-large'>
                Work breakdown
              </Text>
            }
            scale={0.75}
            configurations={getWorkBreakdownConfigurations(
              calcWorkBreakDownData({ newWork, rework, refactor })
            )}
            headLine={{
              value: numeralify(code_changes),
              description: 'CODE CHANGES'
            }}
          >
            <div>
              <PieChart
                data={_.values(workBreakdownConfig)}
                dataKey='percents'
                pieSize={PIE_SIZE}
                noStroke={true}
                showTooltip={false}
              />
            </div>
          </WorkBreakdownPopupTooltip>
        </Flex>
      ),
      newWork: <WorkBreakdownCell {...workBreakdownConfig.newWork} />,
      rework: <WorkBreakdownCell {...workBreakdownConfig.rework} />,
      refactor: <WorkBreakdownCell {...workBreakdownConfig.refactor} />,
      pr_size: <Text size='large'>{numeralify(prSize)}</Text>,
      pr_state:
        prState === EMPTY_VALUE ? (
          <Text size='large' align='center'>
            {EMPTY_VALUE}
          </Text>
        ) : (
          <Chip
            onClick={() => (prUrl ? window.open(prUrl, '_blank') : null)}
            label={prState}
            variant='outlined'
            size='mini'
            isDashed={isDraft}
            color={stateToChipTypeMap[prState]}
          />
        ),
      cycle_time: (
        <CycleTime
          showAccountedTab={
            metricName === metricNames.CYCLE_TIME &&
            isCycleTimeInspectionEnabled
          }
          included_stages={branchesCycleTimeStages[branch.id]}
          branchData={branch}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        />
      ),
      time_to_pr: (
        <CycleTimePipe
          branchData={branch}
          extra={extra}
          location={metricNames.TIME_TO_PR}
          isIncludeWeekends={_.get(
            metricsIncludedWeekend,
            'coding_time',
            false
          )}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        />
      ),
      review_time: (
        <CycleTimePipe
          branchData={branch}
          location={metricNames.REVIEW_TIME}
          isIncludeWeekends={_.get(
            metricsIncludedWeekend,
            'review_time',
            false
          )}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        />
      ),
      time_to_review: (
        <CycleTimePipe
          branchData={branch}
          location={metricNames.TIME_TO_REVIEW}
          isIncludeWeekends={_.get(
            metricsIncludedWeekend,
            'pickup_time',
            false
          )}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        />
      ),
      time_to_prod: (
        <CycleTimePipe
          branchData={branch}
          location={metricNames.TIME_TO_PROD}
          isIncludeWeekends={_.get(
            metricsIncludedWeekend,
            'deploy_time',
            false
          )}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        />
      ),
      ...customStages.reduce((acc, { metric_name }) => {
        return {
          ...acc,
          [metric_name]: (
            <CycleTimePipe branchData={branch} location={metric_name} />
          )
        };
      }, {}),
      ...(isExcludeBranches &&
      !isViewerAccount(myAccount, ACCOUNT_TYPES_MAP.Viewer) &&
      !disableCycleTimeExclusionFunctionality({ branch })
        ? {
            actions: (
              <ActionsMenuCell
                branch={branch}
                toggleModal={toggleModal}
                toggleSnackBar={toggleSnackBar}
                onBranchUpdate={onBranchUpdate}
              />
            )
          }
        : {})
    };
  });
